import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import "./ProcurementPage.css";
import "./PickingQueuePage.css";

const API_BASE =
  "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api";

const PLACEHOLDER_IMG =
  "data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Crect width='120' height='120' fill='%23e0e0e0'/%3E%3Ctext x='50%25' y='50%25' font-size='13' text-anchor='middle' fill='%23999' dy='.3em'%3ENo Image%3C/text%3E%3C/svg%3E";

const STAGES = [
  { key: "received", label: "Product Received" },
  { key: "in_process", label: "Product In Process" },
  { key: "done", label: "Product Repaired" }, // label swapped per-type below
  { key: "handed_over", label: "Product Handed Over" },
];

function authHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Shared list for both "Need Wash" and "Need Repair" — same data shape
// (erpthrift.smartscan.needs_wash / needs_repair), just a different flag +
// a different AI-generated detail field (wash_area vs repair_material).
function CareQueue({ type }) {
  const { companyId, user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [msg, setMsg] = useState(null);
  const fileInputRef = useRef(null);
  const pendingUploadItem = useRef(null);

  const title = type === "wash" ? "Need Wash" : "Need Repair";
  const doneLabel = type === "wash" ? "Product Washed" : "Product Repaired";

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/smartscan/care-queue`, {
        params: {
          company_id: companyId,
          type,
          user_id: user?.id,
          role: user?.role,
        },
        headers: authHeaders(),
      });
      setItems(res.data?.data || []);
    } catch (err) {
      setMsg({ type: "error", text: "Failed to load the care queue." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, type, user?.id, user?.role]);

  const stageIndex = (item) => {
    const idx = STAGES.findIndex(
      (s) => s.key === (item.care_status || "received"),
    );
    return idx === -1 ? 0 : idx;
  };

  const advanceStage = async (item, nextKey) => {
    setBusyId(item.id);
    try {
      await axios.patch(
        `${API_BASE}/smartscan/update-care-status`,
        { smartscan_id: item.id, care_status: nextKey, type },
        { headers: authHeaders() },
      );
      if (nextKey === "handed_over") {
        setItems((prev) => prev.filter((i) => i.id !== item.id));
        setMsg({
          type: "success",
          text: `${item.sku || item.title} handed over.`,
        });
      } else {
        setItems((prev) =>
          prev.map((i) =>
            i.id === item.id ? { ...i, care_status: nextKey } : i,
          ),
        );
      }
    } catch (err) {
      setMsg({ type: "error", text: "Failed to update status — try again." });
    } finally {
      setBusyId(null);
    }
  };

  const openPhotoPicker = (item) => {
    pendingUploadItem.current = item;
    fileInputRef.current?.click();
  };

  const MIN_PHOTOS = 4;

  const handleFilesPicked = async (e) => {
    const files = Array.from(e.target.files || []);
    const item = pendingUploadItem.current;
    e.target.value = "";
    if (!files.length || !item) return;

    // Photos are mandatory — at least 4 — and this upload REPLACES all
    // old photos with the new set (enforced on the backend too).
    if (files.length < MIN_PHOTOS) {
      setMsg({
        type: "error",
        text: `Please select at least ${MIN_PHOTOS} photos — they will replace the old ones.`,
      });
      return;
    }

    setBusyId(item.id);
    try {
      const form = new FormData();
      files.forEach((f) => form.append("images[]", f, f.name));
      const uploadRes = await axios.post(`${API_BASE}/upload-image`, form, {
        timeout: 60000,
      });
      const urls = (uploadRes.data?.images || [])
        .map((i) => i.url)
        .filter(Boolean);
      if (!urls.length) throw new Error("Upload returned no URLs");

      await axios.patch(
        `${API_BASE}/smartscan/update-images`,
        { smartscan_id: item.id, images: urls },
        { headers: authHeaders() },
      );

      setItems((prev) =>
        prev.map((i) =>
          i.id === item.id
            ? {
                ...i,
                image_url: urls[0],
                image_count: urls.length,
                photos_ready: true,
              }
            : i,
        ),
      );
      setMsg({
        type: "success",
        text: `Photos updated for ${item.sku || item.title}.`,
      });
    } catch (err) {
      setMsg({ type: "error", text: "Photo upload failed — try again." });
    } finally {
      setBusyId(null);
    }
  };

  const renderCareDetail = (item) => {
    if (type === "wash") {
      try {
        const areas =
          typeof item.care_detail === "string"
            ? JSON.parse(item.care_detail)
            : item.care_detail;
        return Array.isArray(areas) && areas.length ? areas.join(", ") : null;
      } catch {
        return null;
      }
    }
    return item.care_detail || null;
  };

  return (
    <div className="procurement-page">
      <input
        type="file"
        accept="image/*"
        multiple
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFilesPicked}
      />
      <h1>{title}</h1>
      <p className="subtitle">
        AI-flagged items needing {type === "wash" ? "a wash" : "repair"} —{" "}
        {type === "wash" ? "area" : "material"} is detected automatically by
        Smart Scan. Move each item through its stages; update photos once it's{" "}
        {doneLabel.toLowerCase()}.
      </p>

      {msg && (
        <div className={`proc-msg ${msg.type}`} style={{ marginBottom: 12 }}>
          {msg.text}
        </div>
      )}

      {loading && <div className="pick-empty">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="pick-empty">Nothing here — queue is clear.</div>
      )}

      {items.map((item) => {
        const idx = stageIndex(item);
        const detail = renderCareDetail(item);
        const isBusy = busyId === item.id;
        const nextStage = STAGES[idx + 1];

        return (
          <div
            className="proc-lot-card"
            key={item.id}
            style={{ marginBottom: 14 }}
          >
            <div style={{ display: "flex", gap: 14 }}>
              <img
                src={item.image_url || PLACEHOLDER_IMG}
                alt={item.title || item.sku}
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: 10,
                  objectFit: "cover",
                  background: "#f1f5f9",
                  flexShrink: 0,
                }}
                onError={(e) => {
                  e.target.src = PLACEHOLDER_IMG;
                }}
              />
              <div style={{ flex: 1 }}>
                <div className="pick-location">{item.sku || "—"}</div>
                <div className="pick-meta">
                  {item.title || "Untitled"}
                  {item.brand ? ` · ${item.brand}` : ""}
                  {item.size ? ` · Size ${item.size}` : ""}
                </div>
                {detail && (
                  <div
                    className="pick-meta"
                    style={{ fontSize: 13, color: "#374151", marginTop: 4 }}
                  >
                    <strong>
                      {type === "wash" ? "Wash area: " : "Repair material: "}
                    </strong>
                    {detail}
                  </div>
                )}
                {item.care_reason && (
                  <div
                    className="pick-meta"
                    style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}
                  >
                    {item.care_reason}
                  </div>
                )}
              </div>
            </div>

            <div className="stage-track">
              {STAGES.map((s, i) => (
                <span
                  key={s.key}
                  className={`stage-pill stage-done-${s.key} ${i <= idx ? "is-active" : ""} ${i < idx ? "is-complete" : ""}`}
                >
                  {i < idx ? "✓" : `${i + 1}.`} {i === 2 ? doneLabel : s.label}
                </span>
              ))}
            </div>

            <div className="stage-actions">
              {idx === 2 && (
                <button
                  type="button"
                  className="proc-btn secondary"
                  disabled={isBusy}
                  onClick={() => openPhotoPicker(item)}
                >
                  📷 Update Photos
                  {!item.photos_ready
                    ? ` (upload ${MIN_PHOTOS}+ after this stage)`
                    : ""}
                </button>
              )}
              {nextStage && (
                <>
                  <button
                    type="button"
                    className={`advance-btn to-${nextStage.key}`}
                    disabled={
                      isBusy ||
                      (nextStage.key === "handed_over" && !item.photos_ready)
                    }
                    onClick={() => advanceStage(item, nextStage.key)}
                  >
                    {isBusy
                      ? "…"
                      : `Mark: ${nextStage.key === "done" ? doneLabel : nextStage.label}`}
                  </button>
                  {nextStage.key === "handed_over" && !item.photos_ready && (
                    <div
                      className="pick-meta"
                      style={{ fontSize: 12, color: "#dc2626", marginTop: 4 }}
                    >
                      Upload at least {MIN_PHOTOS} NEW photos (via "Update
                      Photos") before marking as handed over — photos from the
                      original scan don't count.
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export const NeedWashPage = () => <CareQueue type="wash" />;
export const NeedRepairPage = () => <CareQueue type="repair" />;
